<template>
  <div class="pb-2">
    <VSubheader :class="`ride__subheader ${(getIsNightCardSupport && getIsThursday) && 'before-disclaimer'} body-1 px-0`">
      {{ $t(ride.title) }} - {{ formattedDate }}
    </VSubheader>
    <SInfoblock
      v-if="getIsNightCardSupport && getIsThursday && !getIsCheckoutPage && !getIsRideDateIs31DecemberOr1January"
      color="warning-color"
      class="mb-1"
      :no-margin="true"
      :data-test="`nightRiderRideOnThursdayDisclaimer-${index}`"
      :message="$t('nightRider.nightCardSupportOnThursday')"
    />
    <div v-if="!isConfirmationPage" class="ride__time-distance mb-4 d-flex align-center flex-wrap textPrimary--text">
      <span class="mr-4 d-inline-flex align-center flex-wrap">
        <VIcon
          color="brandingSecondary"
          class="mr-2"
        >
          mdi-clock-outline
        </VIcon>
        <span class="body-2">~ {{ tripDuration }} m</span>
      </span>
      <span class="mr-4 d-inline-flex align-center flex-wrap">
        <VIcon
          color="brandingSecondary"
          class="mr-2"
        >
          mdi-map-marker-distance
        </VIcon>
        <span class="body-2">~ {{ ride.distanceKm }} km</span>
      </span>
    </div>
    <div v-if="!isConfirmationPage" class="textSecondary--text body-2">
      <RideInfoLine
        :label="$t(ride.priceTitle)"
        :value="`€ ${pricePerRide|| 0}`"
      />
      <RideInfoLine
        v-if="!!ride.featurePrice"
        :label="$t(ride.featurePriceTitle)"
        :value="`€ ${pricePerFeature || 0}`"
      />
    </div>
    <RideStops
      :rideDetails="ride"
      :orderAsc="orderAsc"
    />
    <SInfoblock
      v-if="ride.rideType === getSharedRideType"
      :message="$t('bookingForm.hints.maxDetourTime', { minutes: ride.maxDetourTime })"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'Ride',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import moment from 'moment-timezone';
import STimeConverter from '@slg/web-customer-shared/src/utils/timeConverter';
import RideTypeEnum from '@slg/web-customer-shared/src/enum/RideTypeEnum';
import SInfoblock from '@slg/web-customer-shared/src/components/common/SInfoblock';
import RideStops from './RideStops.vue';
import RideInfoLine from './RideInfoLine.vue';
import { useRouteRef, useStore } from '../../../composables';

const props = defineProps({
  ride: {
    type: Object,
    required: true,
  },
  orderAsc: {
    type: Boolean,
    default: true,
  },
  index: {
    type: Number,
    default: 0,
  },
  isConfirmationPage: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRouteRef();

const getAppConfig = computed(() => store.getters['common/getAppConfig']);

const formattedDate = computed(() => STimeConverter.convertMilliSecondsToTimeZoneMonthDayFormatWithoutComma(
  props.ride.dateState.time,
  props.ride.timeZone,
  route.value.params.lang
));

const tripDuration = computed(() => STimeConverter.convertMinutesToTime(props.ride.tripDuration));

const pricePerRide = computed(() => Number(props.ride.ridePrice - props.ride.featurePrice || 0).toFixed(
  2
));

const getIsCheckoutPage = computed(() => route.value.name.toLowerCase() === 'checkout');

const getIsNightCardSupport = computed(() => getAppConfig.value?.isNightCardsSupport);

const getIsRideDateIs31DecemberOr1January = computed(() => (moment(props.ride.dateState.time).tz(props.ride.timeZone).month() === 11 && moment(props.ride.dateState.time).tz(props.ride.timeZone).date() === 31)
  || (moment(props.ride.dateState.time).tz(props.ride.timeZone).month() === 0 && moment(props.ride.dateState.time).tz(props.ride.timeZone).date() === 1));

const getIsThursday = computed(() => moment(props.ride.isDepartureExactTime ? props.ride.dateState.time : props.ride.dateState.time - props.ride.timeInMinutes).tz(props.ride.timeZone).day() === 4);

const pricePerFeature = computed(() => Number(props.ride.featurePrice || 0).toFixed(2));

const formattedMaxDetourTime = computed(() => Math.trunc(props.ride.maxDetourTime));

const getSharedRideType = computed(() => RideTypeEnum.SHARED);
</script>

<style lang="scss" scoped>
.ride {
  &__subheader {
    display: block;
    height: 48px;
    text-transform: uppercase;
  }
  &__subheader.before-disclaimer {
    height: 30px;
  }
}
</style>
